import React from "react";
import styled, { css, keyframes } from "styled-components";
import { FileContent, useFilePicker } from "use-file-picker";
import { useTranslation } from "react-i18next";
import { Buffer } from "buffer";
import { ExcuteState } from "features/activation";
import closeIcon from "../../assets/images/icon_close.svg";

interface RevokePopupProps {
  aId: string;
  version: string;
  name: string;
  loading: ExcuteState | undefined;
  onExcute: (aId: string, revocationTicket: ArrayBuffer) => void;
  onCancel: () => void;
}
export const RevokePopup: React.FC<RevokePopupProps> = (
  props: RevokePopupProps
): JSX.Element => {
  const { aId, name, onCancel, onExcute, version, loading } = props;
  const [openFileSelector, { filesContent, clear }] = useFilePicker({
    multiple: false, // ひとつのファイルだけ選択する
    accept: "*",
    readAs: "ArrayBuffer",
  });
  // 複数選択できないようになっているが、もし複数のファイルが来たときは先頭を使用
  const file = filesContent.shift();
  const { t } = useTranslation();

  return (
    <Modal>
      <ModalBox>
        <Title>
          {t("activationPage.label.popupTitle")}
          <button
            type="button"
            onClick={() => {
              if (loading !== "Excuting") {
                clear();
                onCancel();
              }
            }}
          >
            <img src={closeIcon} alt="" />
          </button>
        </Title>
        <form>
          <List>
            <ModalDt>{t("activationPage.popup.activateId")}</ModalDt>
            <ModalDd>{aId}</ModalDd>
            <ModalDt>{t("activationPage.popup.product")}</ModalDt>
            <ModalDd>
              {name} {version}
            </ModalDd>
            <ModalDt>{t("activationPage.popup.revocationTicket")}</ModalDt>
            <ModalDd>
              <SelectArea>
                {
                  // use-file-pickerで取ったファイル名を表示無ければ”No file chosen”
                  file?.name ?? ` ${t("softwarePage.popup.noFileChosen")}`  // softwarePageの翻訳を参照している点に注意
                }
              </SelectArea>
              <SelectFile htmlFor="SelectFileInput">
                <SelectFileInput
                  id="SelectFileInput"
                  onClick={() => openFileSelector()}
                  disabled={loading === "Excuting"}
                />
                {t("softwarePage.popup.selectFile") /* softwarePageの翻訳を参照している点に注意 */}
              </SelectFile>
            </ModalDd>

            <ButtonArea>
              {loading && loading === "Excuting" && <LoadSpinner />}
              <CancelButton
                loading={loading}
                onClick={() => {
                  if (loading !== "Excuting") {
                    clear();
                    onCancel();
                  }
                }}
              >
                {t("common.button.cancel")}
              </CancelButton>
              <ExecuteButton
                type="button"
                loading={loading}
                file={file}
                onClick={() => {
                  // file.contentは常にstringとなっており、stringなのにArrayBufferが入っている状態で取得されるので、
                  // ここでBuffer.from().bufferを渡し、ArrayBufferとして扱うようにする
                  onExcute(aId, Buffer.from(file?.content ?? "").buffer);
                }}
                value={t("common.button.execute")}
              />
            </ButtonArea>
          </List>
        </form>
      </ModalBox>
    </Modal>
  );
};

const Modal = styled.div`
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  z-index: 5;
`;
const ModalBox = styled.div`
  display: block;
  width: 800px;
  padding: 2rem;
  background: #fff;
  box-shadow: 0 0 10px #333;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const Title = styled.p`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3rem;
  font-size: 1.71rem;
  line-height: 2rem;
  letter-spacing: 0.07rem;
  font-weight: normal;
`;
const List = styled.dl`
  display: flex;
  flex-wrap: wrap;
`;
const SelectArea = styled.p`
  display: inline-block;
  width: 250px;
  height: 1.5rem;
  padding: 0.5rem 0.8rem;
  margin-right: 0.6rem;
  border: 1px #e6e6e6 solid;
  vertical-align: middle;
  overflow: hidden;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
`;
const SelectFile = styled.label`
  padding: 0.5rem 1rem;
  background: #e6e6e6;
  cursor: pointer;
`;
const SelectFileInput = styled.input`
  display: none;
`;
const ButtonArea = styled.dd`
  width: 100%;
  padding-top: 2rem;
  text-align: center;
`;
const ExecuteButton = styled.input<{
  loading: ExcuteState | undefined;
  file: FileContent | undefined;
}>`
  width: 10rem;
  padding: 1rem 2rem;
  border: 1px #999999 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  opacity: 0.7;
  pointer-events: none;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  ${({ loading, file }) =>
    loading !== "Excuting" &&
    file !== undefined &&
    css`
      cursor: pointer;
      opacity: 1;
      color: #ffffff;
      background: #005eb8;
      border: 1px #005eb8 solid;
      pointer-events: auto;
      :hover {
        background: rgba(0, 94, 184, 0.7);
      }
    `}
`;
const CancelButton = styled.p<{ loading: ExcuteState | undefined }>`
  color: #999999;
  text-decoration: none;
  cursor: inherit;
  display: inline-block;
  margin: 0 3rem;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  ${({ loading }) =>
    loading !== "Excuting" &&
    css`
      color: #005eb8;
      text-decoration: underline;
      cursor: pointer;
    `}
`;
const ModalDt = styled.dt`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  width: 20%;
  padding: 1rem 2%;
`;
const ModalDd = styled.dd`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  width: 76%;
  padding: 1rem 0;
`;
const spin = keyframes`
 from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
const LoadSpinner = styled.p`
  position: absolute;
  bottom: 3rem;
  left: 50%;
  transform: translateX(-50%);

  width: 50px;
  height: 50px;
  margin: 40px auto;
  border: 3px solid #e6e6e6;
  border-top: 3px solid transparent;
  border-radius: 50%;
  animation: ${spin} 0.8s linear 0s infinite;
`;
